/**
 * Сессии секретных чатов (изменение v1.13): установление по набору ключей
 * собеседника, шифрование исходящих и расшифровка входящих конвертов.
 *
 * Всё состояние — в `secret_sessions` пользовательской базы; каждое изменение
 * идёт под замком диалога (`withSecretLock`), потому что приём и отправка
 * могут идти из разных вкладок. Расшифровка никогда не бросает наружу
 * криптографическую ошибку: нечитаемое сообщение остаётся в истории с
 * пометкой, а состояние рэтчета не сдвигается.
 */
import type { Message, SecretKeyBundle } from '@shared/api/types'
import { fromBase64 } from '@shared/crypto/bytes'
import { decodeEnvelope } from '@shared/crypto/envelope'
import type { SecretPayload } from '@shared/crypto/payload'
import {
  acceptSession,
  decryptMessage,
  encryptMessage,
  openSession,
  type SecretSession,
} from '@shared/crypto/session'
import type { PrekeyBundle } from '@shared/crypto/x3dh'
import {
  messagesRepo,
  secretKeysRepo,
  secretSessionsRepo,
  type ConfirmedMessage,
  type LocalSecretMeta,
} from '@shared/db'

import { ensureLocalIdentity, localIdentity } from './keys'
import { withSecretLock } from './lock'

export type DecryptedMessage = ConfirmedMessage & { local_secret: LocalSecretMeta }

function decodeBundle(bundle: SecretKeyBundle): PrekeyBundle {
  return {
    deviceId: bundle.device_id,
    signingKey: fromBase64(bundle.signing_key),
    identityKey: fromBase64(bundle.identity_key),
    identitySignature: fromBase64(bundle.identity_signature),
    signedPrekey: {
      id: bundle.signed_prekey.id,
      key: fromBase64(bundle.signed_prekey.key),
      signature: fromBase64(bundle.signed_prekey.signature),
    },
    oneTimePrekey: bundle.one_time_prekey
      ? { id: bundle.one_time_prekey.id, key: fromBase64(bundle.one_time_prekey.key) }
      : null,
  }
}

/** Инициатор: сервер выдал набор ключей устройства собеседника — сессия готова к отправке. */
export async function startSecretSession(
  conversationId: string,
  peerUserId: string,
  bundle: SecretKeyBundle,
): Promise<void> {
  const { identity } = await ensureLocalIdentity()
  const session = await openSession(identity, decodeBundle(bundle))
  await withSecretLock(conversationId, () =>
    secretSessionsRepo.put({
      conversation_id: conversationId,
      peer_user_id: peerUserId,
      session,
      ttl: 0,
      updated_at: new Date().toISOString(),
    }),
  )
}

export async function hasSecretSession(conversationId: string): Promise<boolean> {
  return Boolean(await secretSessionsRepo.get(conversationId))
}

export async function currentTtl(conversationId: string): Promise<number> {
  return (await secretSessionsRepo.get(conversationId))?.ttl ?? 0
}

export async function rememberTtl(conversationId: string, ttl: number): Promise<void> {
  await secretSessionsRepo.setTtl(conversationId, ttl)
}

export async function forgetSecretSession(conversationId: string): Promise<void> {
  await secretSessionsRepo.delete(conversationId)
}

/** Ключи собеседника, зафиксированные сессией, — для экрана «Ключ шифрования». */
export async function secretSessionInfo(
  conversationId: string,
): Promise<{ peerUserId: string; remoteSigningKey: Uint8Array; remoteIdentityKey: Uint8Array } | null> {
  const record = await secretSessionsRepo.get(conversationId)
  if (!record) return null
  return {
    peerUserId: record.peer_user_id,
    remoteSigningKey: record.session.remoteSigningKey,
    remoteIdentityKey: record.session.remoteIdentityKey,
  }
}

/** Шифрует полезную нагрузку и сохраняет продвинутую сессию; конверт — Base64 для контракта. */
export async function encryptOutgoing(conversationId: string, payload: SecretPayload): Promise<string> {
  return withSecretLock(conversationId, async () => {
    const record = await secretSessionsRepo.get(conversationId)
    if (!record) throw new Error('secret_session_missing')
    const result = await encryptMessage(record.session, payload)
    await secretSessionsRepo.put({ ...record, session: result.session, updated_at: new Date().toISOString() })
    return result.envelope
  })
}

async function replyPreviewFor(replyToId: string | null | undefined): Promise<Message['reply_to']> {
  if (!replyToId) return null
  const original = await messagesRepo.byId(replyToId)
  if (!original) return null
  return {
    id: original.id,
    seq: original.seq,
    sender_id: original.sender_id ?? '',
    sender: original.sender ?? null,
    type: 'text',
    text_preview: original.text?.slice(0, 120) ?? null,
    deleted: original.deleted,
  }
}

async function decorate(message: Message, payload: SecretPayload): Promise<DecryptedMessage> {
  switch (payload.kind) {
    case 'text': {
      const text = (payload as Extract<SecretPayload, { kind: 'text' }>).text
      const ttl = (payload as Extract<SecretPayload, { kind: 'text' }>).ttl
      const replyTo = (payload as Extract<SecretPayload, { kind: 'text' }>).reply_to?.id ?? null
      return {
        ...message,
        text,
        reply_to: await replyPreviewFor(replyTo),
        local_secret: { kind: 'text', ttl, media: null, reply_to_id: replyTo },
      }
    }
    case 'media': {
      const media = payload as Extract<SecretPayload, { kind: 'media' }>
      const replyTo = media.reply_to?.id ?? null
      return {
        ...message,
        text: media.text ?? null,
        reply_to: await replyPreviewFor(replyTo),
        local_secret: { kind: 'media', ttl: media.ttl, media: media.media, reply_to_id: replyTo },
      }
    }
    case 'timer': {
      const ttl = (payload as Extract<SecretPayload, { kind: 'timer' }>).ttl
      return { ...message, text: null, local_secret: { kind: 'timer', ttl, media: null, reply_to_id: null } }
    }
    default:
      return {
        ...message,
        text: null,
        local_secret: { kind: 'unknown', ttl: 0, media: null, reply_to_id: null },
      }
  }
}

function unreadable(message: Message, error: unknown): DecryptedMessage {
  const code = error instanceof Error ? error.message : 'secret_decrypt_failed'
  return {
    ...message,
    text: null,
    local_secret: { kind: 'unreadable', ttl: 0, media: null, reply_to_id: null, error: code },
  }
}

/**
 * Входящее сообщение собеседника. Первое сообщение несёт X3DH-заголовок: по нему и
 * своим prekeys устройство выводит общий секрет и открывает сессию; дальше работает
 * рэтчет. Расшифрованное содержимое возвращается полями сообщения — сервер их не знает.
 */
export async function decryptIncoming(message: Message, myUserId: string): Promise<DecryptedMessage> {
  if (message.sender_id === myUserId || !message.envelope)
    return unreadable(message, new Error('secret_envelope_missing'))
  const envelope = message.envelope
  return withSecretLock(message.conversation_id, async () => {
    try {
      const record = await secretSessionsRepo.get(message.conversation_id)
      const now = new Date().toISOString()
      if (record) {
        const result = await decryptMessage(record.session, envelope)
        await secretSessionsRepo.put({ ...record, session: result.session, updated_at: now })
        const decorated = await decorate(message, result.payload)
        if (decorated.local_secret.kind === 'timer')
          await secretSessionsRepo.setTtl(message.conversation_id, decorated.local_secret.ttl)
        return decorated
      }
      const identity = await localIdentity()
      if (!identity) throw new Error('secret_identity_missing')
      const header = decodeEnvelope(envelope).initial
      if (!header) throw new Error('secret_session_missing')
      const signed = await secretKeysRepo.prekey('signed', header.signedPrekeyId)
      if (!signed) throw new Error('secret_prekey_missing')
      const oneTime =
        header.oneTimePrekeyId === null
          ? null
          : await secretKeysRepo.prekey('one_time', header.oneTimePrekeyId)
      if (header.oneTimePrekeyId !== null && !oneTime) throw new Error('secret_prekey_missing')
      const accepted = await acceptSession(
        identity,
        {
          signedPrekeyPrivate: signed.private_key,
          signedPrekeyPublic: signed.public_key,
          oneTimePrekeyPrivate: oneTime?.private_key ?? null,
        },
        envelope,
      )
      const decorated = await decorate(message, accepted.payload)
      const session: SecretSession = accepted.session
      await secretSessionsRepo.put({
        conversation_id: message.conversation_id,
        peer_user_id: message.sender_id ?? '',
        session,
        ttl:
          decorated.local_secret.kind === 'timer' ? decorated.local_secret.ttl : decorated.local_secret.ttl,
        updated_at: now,
      })
      // Одноразовый ключ использован — второй сессии на нём не будет.
      if (oneTime) await secretKeysRepo.deletePrekey('one_time', oneTime.id)
      return decorated
    } catch (error) {
      return unreadable(message, error)
    }
  })
}
